import { BillingInterval, billingPrice } from "./BillingInterval";
import { ALL_ACCESS_PRICE_MAP, isAllAccessType } from "./allAccess";
import type { AccountPlan } from "./api";
import type { Subscription, Type } from "./types";

export const TRIAL_SPORTS_API_RATE_LIMIT = 5;
export const DEFAULT_FULL_SPORTS_API_RATE_LIMIT = 600;

type BuildTrialUpgradeConfirmationParams = {
  type: Type;
  subscription?: Subscription;
  accountPlans?: AccountPlan[];
  billingInterval?: BillingInterval;
  currentBillingInterval?: BillingInterval;
};

export type TrialUpgradeConfirmationContent = {
  packageName: string;
  chargeDescription: string;
  trialRateLimitLabel: string;
  fullRateLimitLabel: string;
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);

const monthlyPriceFor = (type: Type, accountPlans?: AccountPlan[]) => {
  const planPrice = accountPlans?.find((plan) => plan.sport === type)?.tiers
    .paid_plus?.price;
  if (typeof planPrice === "number") return planPrice;
  if (isAllAccessType(type)) return ALL_ACCESS_PRICE_MAP[type] ?? 299.99;
  return 39.99;
};

const fullRateLimitFor = (type: Type, accountPlans?: AccountPlan[]) => {
  const planLimit = accountPlans?.find((plan) => plan.sport === type)?.tiers
    .paid_plus?.rate_limit;
  return typeof planLimit === "number"
    ? planLimit
    : DEFAULT_FULL_SPORTS_API_RATE_LIMIT;
};

export const buildTrialUpgradeConfirmationContent = ({
  type,
  subscription,
  accountPlans,
  billingInterval = "month", currentBillingInterval = "month",
}: BuildTrialUpgradeConfirmationParams): TrialUpgradeConfirmationContent => {
  const monthlyAmount = monthlyPriceFor(type, accountPlans);
  const monthlyLabel = `${formatCurrency(monthlyAmount)}/mo`;
  const immediateChargeLabel = formatCurrency(monthlyAmount);
  const packageName = isAllAccessType(type) ? "ALL-ACCESS" : "GOAT";
  const isPaidUpgradeOverlay =
    subscription?.trialSource === "paid_upgrade_overlay";

  return {
    packageName,
    chargeDescription: billingInterval === "year" ? (isPaidUpgradeOverlay ? `Your GOAT plan is ${billingPrice(type, "paid+", "year")}. ${currentBillingInterval === "year" ? "You pay the prorated difference and keep your renewal date." : "Your annual term starts now, with credit for unused ALL-STAR time."}` : `Your card will be charged ${billingPrice(type, "paid+", "year")} starting now, and renew annually.`) : isPaidUpgradeOverlay
      ? `You'll be invoiced now for the prorated difference between ALL-STAR and GOAT for the rest of your current billing period. Future renewals stay on your current renewal date at ${monthlyLabel}.`
      : `Your card will be charged ${immediateChargeLabel} immediately, and your subscription will renew monthly at ${monthlyLabel}.`,
    trialRateLimitLabel: `${TRIAL_SPORTS_API_RATE_LIMIT} sports API req/min`,
    fullRateLimitLabel: `${fullRateLimitFor(
      type,
      accountPlans
    )} sports API req/min`,
  };
};
